import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { take } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ProductoCementoUpsertCommand } from '../../../models/producto-cemento.model';
import { ProductoCementoVm } from '../../../models/producto-cemento.vm';
import { ProductosCementoStore } from '../../../stores/productos-cemento.store';
import { ProductosCementoUiEffectsService } from '../ui-effects/productos-cemento-ui-effects.service';
import {
  AdminServerTableCellContext,
  AdminServerTableColumn,
  AdminServerTablePaginationState,
} from '../../../shared/table/models/admin-server-table.model';
import { buildProductosCementoColumns } from './productos-cemento-table.columns';

const MODAL_CONFIRMACION: { centered: true; backdrop: 'static'; windowClass: string } = {
  centered: true,
  backdrop: 'static',
  windowClass: 'cbb-admin-modal-stack',
};

@Component({
  selector: 'app-productos-cemento-tabla',
  templateUrl: './productos-cemento-tabla.component.html',
  styleUrls: ['./productos-cemento-tabla.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductosCementoTablaComponent implements OnInit {
  @ViewChild('detalleTpl', { static: true }) detalleTpl!: TemplateRef<unknown>;
  @ViewChild('cargaMasivaTpl', { static: true }) cargaMasivaTpl!: TemplateRef<unknown>;
  @ViewChild('confirmDeleteTpl', { static: true }) confirmDeleteTpl!: TemplateRef<unknown>;
  @ViewChild('confirmGuardarTpl', { static: true }) confirmGuardarTpl!: TemplateRef<unknown>;
  @ViewChild('priceCellTpl', { static: true })
  priceCellTpl!: TemplateRef<AdminServerTableCellContext<ProductoCementoVm>>;
  @ViewChild('actionsCellTpl', { static: true })
  actionsCellTpl!: TemplateRef<AdminServerTableCellContext<ProductoCementoVm>>;

  readonly store = inject(ProductosCementoStore);
  private readonly modalService = inject(NgbModal);
  private readonly uiEffects = inject(ProductosCementoUiEffectsService);

  private detalleModalRef: NgbModalRef | null = null;
  private cargaMasivaModalRef: NgbModalRef | null = null;
  private confirmDeleteModalRef: NgbModalRef | null = null;
  private confirmGuardarModalRef: NgbModalRef | null = null;
  private pendingUpsert: ProductoCementoUpsertCommand | null = null;
  readonly tableColumns: AdminServerTableColumn<ProductoCementoVm>[] = [];

  selectedItem: ProductoCementoVm | null = null;

  ngOnInit(): void {
    this.tableColumns.push(
      ...buildProductosCementoColumns({
        priceCellTemplate: this.priceCellTpl,
        actionsCellTemplate: this.actionsCellTpl,
      }),
    );
  }

  get tituloConfirmarGuardar(): string {
    return (this.selectedItem?.id ?? 0) > 0 ? 'Confirmar cambios' : 'Confirmar creación';
  }

  get mensajeConfirmarGuardar(): string {
    return (this.selectedItem?.id ?? 0) > 0
      ? '¿Desea guardar los cambios de este producto?'
      : '¿Desea crear este producto?';
  }

  get etiquetaConfirmarGuardar(): string {
    return (this.selectedItem?.id ?? 0) > 0 ? 'Guardar' : 'Crear';
  }

  abrirModalCargaMasiva(): void {
    const ref = this.modalService.open(this.cargaMasivaTpl, { centered: true });
    this.cargaMasivaModalRef = ref;
    ref.hidden.pipe(take(1)).subscribe(() => {
      if (this.cargaMasivaModalRef === ref) {
        this.cargaMasivaModalRef = null;
      }
    });
  }

  subirExcelCargaMasiva(file: File): void {
    this.store.subirExcelCargaMasiva(file).subscribe({
      next: () => {
        this.cargaMasivaModalRef?.dismiss();
        this.cargaMasivaModalRef = null;
        this.recargar(() => {
          this.uiEffects.showExcelSuccess();
        });
      },
      error: () => {
        this.uiEffects.showExcelError();
      },
    });
  }

  cerrarCargaMasivaModal(): void {
    this.cargaMasivaModalRef?.dismiss();
    this.cargaMasivaModalRef = null;
  }

  abrirCrear(): void {
    this.selectedItem = null;
    this.openDetalleModal();
  }

  abrirEditar(item: ProductoCementoVm): void {
    this.selectedItem = item;
    this.openDetalleModal();
  }

  prepararEliminar(item: ProductoCementoVm): void {
    this.selectedItem = item;
    this.solicitarEliminar();
  }

  guardar(item: ProductoCementoUpsertCommand): void {
    this.pendingUpsert = item;
    this.confirmGuardarModalRef = this.modalService.open(this.confirmGuardarTpl, MODAL_CONFIRMACION);
  }

  confirmarGuardar(): void {
    const item = this.pendingUpsert;
    const selectedItem = this.selectedItem;
    this.pendingUpsert = null;
    this.confirmGuardarModalRef?.dismiss();
    this.confirmGuardarModalRef = null;

    if (!item) {
      return;
    }

    const request$ = selectedItem
      ? this.store.editarProducto(selectedItem.id, item)
      : this.store.crearProducto(item);

    this.detalleModalRef?.dismiss();
    this.detalleModalRef = null;

    request$.subscribe({
      next: () => {
        this.recargar(() => {
          if (selectedItem) {
            this.uiEffects.showUpdateSuccess();
            return;
          }
          this.uiEffects.showCreateSuccess();
        });
      },
      error: () => {
        if (selectedItem) {
          this.uiEffects.showUpdateError();
          return;
        }
        this.uiEffects.showCreateError();
      },
    });
  }

  cancelarGuardar(): void {
    this.pendingUpsert = null;
    this.confirmGuardarModalRef?.dismiss();
    this.confirmGuardarModalRef = null;
  }

  solicitarEliminar(): void {
    if ((this.selectedItem?.id ?? 0) < 1) {
      return;
    }

    this.confirmDeleteModalRef = this.modalService.open(this.confirmDeleteTpl, MODAL_CONFIRMACION);
  }

  confirmarEliminar(): void {
    const id = this.selectedItem?.id ?? 0;
    this.confirmDeleteModalRef?.dismiss();
    this.confirmDeleteModalRef = null;

    if (id < 1 || this.store.loading()) {
      return;
    }

    this.detalleModalRef?.dismiss();
    this.detalleModalRef = null;
    this.store.eliminarProducto(id).subscribe({
      next: () => {
        this.recargar(() => {
          this.uiEffects.showDeleteSuccess();
        });
      },
      error: () => {
        this.uiEffects.showDeleteError();
      },
    });
  }

  cancelarEliminar(): void {
    this.confirmDeleteModalRef?.dismiss();
    this.confirmDeleteModalRef = null;
  }

  cerrarDetalle(): void {
    this.detalleModalRef?.dismiss();
    this.detalleModalRef = null;
    this.selectedItem = null;
  }

  onSearchTermChange(value: string | number): void {
    this.store.setSearchTerm(String(value ?? ''));
  }

  onOrderingChange(value: string): void {
    this.store.setOrdering(value);
  }

  onPageChange(value: number): void {
    this.store.setPage(value);
  }

  onPageSizeChange(value: number): void {
    this.store.setPageSize(value);
  }

  get paginationState(): AdminServerTablePaginationState {
    return {
      page: this.store.page(),
      pageSize: this.store.pageSize(),
      total: this.store.totalItems(),
      totalPages: this.store.totalPages(),
      hasPreviousPage: this.store.hasPreviousPage(),
      hasNextPage: this.store.hasNextPage(),
      pageStartIndex: this.store.pageStartIndex(),
      pageEndIndex: this.store.pageEndIndex(),
      pageSizeOptions: [10, 20, 50, 100],
    };
  }

  readonly trackByItem = (_: number, item: ProductoCementoVm): string => item.trackId;

  private openDetalleModal(): void {
    const ref = this.modalService.open(this.detalleTpl, {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    });
    this.detalleModalRef = ref;
    ref.hidden.pipe(take(1)).subscribe(() => {
      if (this.detalleModalRef === ref) {
        this.detalleModalRef = null;
      }
      this.selectedItem = null;
    });
  }

  private recargar(onSuccess?: () => void): void {
    this.store.clearError();
    this.store.loadProductos().subscribe({
      next: () => {
        onSuccess?.();
      },
      error: () => {
        this.uiEffects.showLoadError();
      },
    });
  }
}
